// TEN18 (docs/build-briefs/TEN18-publish-discounts-adopt-by-receipt.md; ADR-008, P0.21): how a published discount's
// terms are written in words and typed in, shared by the franchisor console (publishing) and the location's Settings
// (the "From <network name>" offers) so the two pages can never describe the same discount differently. Pure and
// DOM-free: test/ten18-publications-ui-model.test.ts proves it. Whole basis points and whole cents only, never
// floating point money.
import { normalizeLeadingDot } from "../../src/domain/leadingDot.ts";

export type MarkdownKind = "percentage" | "amount";

export interface DiscountTerms {
  markdownType: MarkdownKind;
  markdownBasisPoints: number | null;
  markdownAmountCents: number | null;
  appliesToTickets: boolean;
  appliesToItems: boolean;
  requiresApproval: boolean;
  requiresReason: boolean;
}

/** "10%" from 1000, "12.5%" from 1250, "12.05%" from 1205. Integer arithmetic only. */
export function formatPercent(basisPoints: number): string {
  const whole = Math.floor(basisPoints / 100);
  const fraction = basisPoints % 100;
  if (fraction === 0) return `${whole}%`;
  const digits = String(fraction).padStart(2, "0").replace(/0$/, "");
  return `${whole}.${digits}%`;
}

/** "$5.00" from 500, "$1,234.50" from 123450. */
export function formatDollars(rawCents: number): string {
  const cents = Math.round(rawCents);
  const whole = Math.floor(cents / 100);
  const fraction = String(cents % 100).padStart(2, "0");
  return `$${whole.toLocaleString("en-US")}.${fraction}`;
}

export function amountOffText(
  terms: Pick<DiscountTerms, "markdownType" | "markdownBasisPoints" | "markdownAmountCents">,
): string {
  if (terms.markdownType === "percentage") {
    return terms.markdownBasisPoints === null ? "Percent off" : `${formatPercent(terms.markdownBasisPoints)} off`;
  }
  return terms.markdownAmountCents === null ? "Dollars off" : `${formatDollars(terms.markdownAmountCents)} off`;
}

export function appliesToText(terms: Pick<DiscountTerms, "appliesToTickets" | "appliesToItems">): string {
  if (terms.appliesToTickets && terms.appliesToItems) return "tickets and items";
  if (terms.appliesToTickets) return "tickets";
  if (terms.appliesToItems) return "items";
  return "nothing";
}

export function rulesText(terms: Pick<DiscountTerms, "requiresApproval" | "requiresReason">): string {
  if (terms.requiresApproval && terms.requiresReason) return "Needs approval and a reason";
  if (terms.requiresApproval) return "Needs approval";
  if (terms.requiresReason) return "Needs a reason";
  return "";
}

/** "10% off tickets", "$5.00 off tickets and items. Needs approval". */
export function termsSummary(terms: DiscountTerms): string {
  const head = `${amountOffText(terms)} ${appliesToText(terms)}`;
  const rules = rulesText(terms);
  return rules === "" ? head : `${head}. ${rules}`;
}

export const PERCENT_AMOUNT_MESSAGE = "Enter a percentage above 0 and up to 100, for example 10 or 12.5.";
export const DOLLAR_AMOUNT_MESSAGE = "Enter a dollar amount above 0, for example 5 or 5.50.";

export type AmountParse =
  | { ok: true; markdownBasisPoints: number | null; markdownAmountCents: number | null }
  | { ok: false; message: string };

/** Parses the Amount box for the chosen type into whole basis points or whole cents. */
export function parseAmountInput(type: MarkdownKind, text: string): AmountParse {
  const trimmed = normalizeLeadingDot(normalizeLeadingDot(text).replace(/^\$/, "").replace(/%$/, ""));
  if (type === "percentage") {
    const match = /^(\d{1,3})(?:\.(\d{1,2}))?$/.exec(trimmed);
    if (match === null) return { ok: false, message: PERCENT_AMOUNT_MESSAGE };
    const bps = Number(match[1]) * 100 + Number((match[2] ?? "").padEnd(2, "0") || "0");
    if (bps < 1 || bps > 10000) return { ok: false, message: PERCENT_AMOUNT_MESSAGE };
    return { ok: true, markdownBasisPoints: bps, markdownAmountCents: null };
  }
  const match = /^(\d{1,7})(?:\.(\d{1,2}))?$/.exec(trimmed);
  if (match === null) return { ok: false, message: DOLLAR_AMOUNT_MESSAGE };
  const cents = Number(match[1]) * 100 + Number((match[2] ?? "").padEnd(2, "0") || "0");
  if (cents < 1) return { ok: false, message: DOLLAR_AMOUNT_MESSAGE };
  return { ok: true, markdownBasisPoints: null, markdownAmountCents: cents };
}

export function isDiscountTermsShape(value: unknown): value is DiscountTerms {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  const kind = v["markdownType"];
  if (kind !== "percentage" && kind !== "amount") return false;
  const bps = v["markdownBasisPoints"];
  const cents = v["markdownAmountCents"];
  if (
    kind === "percentage" &&
    !(typeof bps === "number" && Number.isInteger(bps) && bps >= 1 && bps <= 10000 && cents === null)
  ) {
    return false;
  }
  if (kind === "amount" && !(typeof cents === "number" && Number.isInteger(cents) && cents >= 1 && bps === null)) {
    return false;
  }
  return (
    typeof v["appliesToTickets"] === "boolean" &&
    typeof v["appliesToItems"] === "boolean" &&
    typeof v["requiresApproval"] === "boolean" &&
    typeof v["requiresReason"] === "boolean"
  );
}
